import { toEthereumAddress, toStreamPartID } from '@streamr/utils';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import range from 'lodash/range';
import { getPartitionCount } from './StreamMetadata';
import { toInternalPermissionAssignment } from './permission';
/**
 * A convenience API for managing and accessing an individual stream.
 *
 * @category Important
 */
export class Stream {
    id;
    client;
    /** @internal */
    constructor(id, client) {
        this.id = id;
        this.client = client;
    }
    /**
     * See {@link StreamrClient.publish | StreamrClient.publish}.
     *
     * @category Important
     */
    publish(content, metadata) {
        return this.client.publish(this.id, content, metadata);
    }
    /**
     * See {@link StreamrClient.hasPermission | StreamrClient.hasPermission}.
     *
     * @category Important
     */
    async hasPermission(query) {
        return this.client.hasPermission({
            streamId: this.id,
            ...query
        });
    }
    /**
     * See {@link StreamrClient.getPermissions | StreamrClient.getPermissions}.
     *
     * @category Important
     */
    async getPermissions() {
        return this.client.getPermissions(this.id);
    }
    /**
     * See {@link StreamrClient.grantPermissions | StreamrClient.grantPermissions}.
     *
     * @category Important
     */
    async grantPermissions(...assignments) {
        return this.client.grantPermissions(this.id, ...assignments.map(toInternalPermissionAssignment));
    }
    /**
     * See {@link StreamrClient.revokePermissions | StreamrClient.revokePermissions}.
     *
     * @category Important
     */
    async revokePermissions(...assignments) {
        return this.client.revokePermissions(this.id, ...assignments.map(toInternalPermissionAssignment));
    }
    /**
     * See {@link StreamrClient.addStreamToStorageNode | StreamrClient.addStreamToStorageNode}.
     *
     * @category Important
     */
    async addToStorageNode(storageNodeAddress, opts = { wait: false }) {
        await this.client.addStreamToStorageNode(this.id, storageNodeAddress, opts);
    }
    /**
     * See {@link StreamrClient.removeStreamFromStorageNode | StreamrClient.removeStreamFromStorageNode}.
     */
    async removeFromStorageNode(nodeAddress) {
        return this.client.removeStreamFromStorageNode(this.id, toEthereumAddress(nodeAddress));
    }
    /**
     * See {@link StreamrClient.getStorageNodes | StreamrClient.getStorageNodes}.
     */
    async getStorageNodes() {
        return this.client.getStorageNodes(this.id);
    }
    /**
     * Returns the partitions of the stream.
     */
    async getStreamParts() {
        return range(0, await this.getPartitionCount()).map((p) => toStreamPartID(this.id, p));
    }
    async getPartitionCount() {
        return getPartitionCount(await this.getMetadata());
    }
    async getDescription() {
        const value = (await this.getMetadata()).description;
        if (isString(value)) {
            return value;
        }
        else {
            return undefined;
        }
    }
    async setDescription(description) {
        await this.setMetadata({
            ...await this.getMetadata(),
            description
        });
    }
    /**
     * Gets the value of `storageDays` field
     */
    async getStorageDayCount() {
        const value = (await this.getMetadata()).storageDays;
        if (isNumber(value)) {
            return value;
        }
        else {
            return undefined;
        }
    }
    /**
     * Sets the value of `storageDays` field
     */
    async setStorageDayCount(count) {
        await this.setMetadata({
            ...await this.getMetadata(),
            storageDays: count
        });
    }
    /**
     * Returns the metadata of the stream.
     */
    async getMetadata() {
        return this.client.getStreamMetadata(this.id);
    }
    /**
     * Updates the metadata of the stream.
     */
    async setMetadata(metadata) {
        await this.client.setStreamMetadata(this.id, metadata);
    }
}
